import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useFinancials } from './editor';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { OverBudgetAlert } from '@/components/ui';
import { formatAmountAbbrev } from '@/lib';

/**
 * CALC-14: distributed amounts exceed the contract amount. Also surfaces money
 * trapped in a category the budget-model config removed (BR-27) — such amounts
 * drop out of the distribution (CALC-19) and would otherwise silently read as
 * not-allocated.
 */
export const OverBudgetBanner = () => {
  const { state, activeFinancials } = useFinancials();
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);

  const trapped = activeFinancials.categories.filter(
    (category) => !category.available && category.amount > 0,
  );
  const trappedNotice =
    trapped.length === 0 ? null : (
      <Box
        data-automation-id="trapped-amount-notice"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: 'var(--color-warning-10)',
          border: 'var(--border-width-thin) solid var(--border-divider)',
          borderRadius: 'var(--border-radius-small)',
          mt: 1,
        }}
      >
        <MaterialSymbol name="info" size={16} color="var(--color-warning-80)" />
        <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-2)' }}>
          {trapped
            .map((category) =>
              t('overBudget.Trapped', {
                amount: formatAmountAbbrev(category.amount),
                category: t(`pmCommon:category.${category.id}`),
              }),
            )
            .join(' · ')}{' '}
          {t('overBudget.TrappedSuffix')}
        </Box>
      </Box>
    );

  if (!activeFinancials.overDistributed) return trappedNotice;

  const overBy = -activeFinancials.notAllocated;
  const overPct =
    state.document.revenue > 0 ? Math.round((overBy / state.document.revenue) * 100) : 100;

  return (
    <>
      <OverBudgetAlert
        automationId="over-budget-banner"
        title={t('overBudget.OverBy', { pct: overPct, amount: formatAmountAbbrev(overBy) })}
        advice={t('overBudget.Advice')}
      />
      {trappedNotice}
    </>
  );
};
